import { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import { ReportMasthead } from "../reports/ReportDocument.jsx";

// A subject line and a covering note for anything that gets printed and sent
// out — the POW to a contractor today, job sheets next.
//
// THE 1.0 VERSION, AND WHY THIS IS NOT A PORT OF IT.
//
// 1.0 does this with `PrintPage.aspx`: a whole separate page that receives a
// rendered document, wraps it in letterhead, and offers an editable title plus a
// rich-text block to paste a covering note into. It exists because 1.0 could not
// print its own pages — no print stylesheet, no way to drop the chrome — so
// instead of making each page printable it made ONE page that prints anything
// handed to it. Robert wrote it and does not like it ("OMG I hate all the
// additions… I think I did a full-on duplication of it").
//
// 2.0 prints its own pages, so the workaround is not needed — only the
// capability inside it. Hence: INLINE, on the page it belongs to. Nothing is
// serialised across a page boundary, nothing is rendered twice, and there is no
// second copy of the document to drift. That boundary is what forced 1.0 into
// duplicating the calendar in the first place.
//
// DELIBERATELY NOT PORTED (Robert 2026-08-11, "some compromise needs to be had"):
//
//   · The rich-text editor (bold/italic/link/image/H1/H2/lists, "paste your
//     content including images"). That is a small CMS living inside a print
//     page, and it brings paste sanitisation and a lifetime of odd-looking
//     documents with it. A plain note does the job the note is for.
//   · "Title Text Size" and "Bold". Per-print typography controls are how five
//     differently-styled documents leave the building under one letterhead. The
//     document has one correct size, chosen here, once.
//
// STATE IS EPHEMERAL, exactly as 1.0's is: you type a subject when you print.
// No column, no migration. If it turns out people retype the same subject every
// week, that is the moment to persist it — and by then we will know which
// document actually needs it.
function PrintHeader({
  defaultTitle = "",
  subtitle = "Grayburn Property Services",
  noteLabel = "Note (optional)",
  className = "mb-2",
}) {
  const [title, setTitle] = useState(defaultTitle);
  const [note, setNote] = useState("");
  // The user has typed in the title box, so stop overwriting it.
  const [edited, setEdited] = useState(false);

  // `defaultTitle` ARRIVES LATE, and a plain useState initialiser would miss it.
  // ClaimPowTab builds the title from the claim, which ClaimWorkspace fetches
  // asynchronously — so on a fresh load straight onto the POW tab the first
  // render has `claim = null` and the default is the bare "POW Calendar". Frozen
  // at mount, the printed masthead then carries no job number, no client and no
  // address, which is precisely the thing Karen asked for; and because the tab
  // supplies its own header, ClaimWorkspace suppresses the fallback identity
  // line for it, so nothing else names the job either.
  //
  // Following it until the user types keeps both halves true: the title fills
  // itself in when the claim lands, and anything typed is never clobbered by a
  // later re-render.
  useEffect(() => {
    if (!edited) setTitle(defaultTitle);
  }, [defaultTitle, edited]);

  return (
    <>
      {/* The controls. On screen only — they are how you fill the document in,
          and they are not part of it. */}
      <details className={`no-print pow-print-header ${className}`}>
        <summary className="small fw-semibold" style={{ cursor: "pointer" }}>
          Print header
        </summary>
        {/* position:absolute while open (see calendar.css). The summary sits in
            a row beside Display by and Extended date range, and letting the
            expanded fields grow inside that flex item would shove its
            neighbours around every time somebody opened it. Taking the panel
            out of flow means the row never moves. */}
        <div className="pow-print-header-panel mt-2 d-flex flex-column gap-2">
          <Form.Control
            size="sm"
            value={title}
            onChange={(e) => {
              setEdited(true);
              setTitle(e.target.value);
            }}
            placeholder="Subject / title for the printed page"
            aria-label="Printed title"
          />
          <Form.Control
            size="sm"
            as="textarea"
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={noteLabel}
            aria-label="Printed note"
          />
          {/* NO print button here, and none in the tab toolbar either (Robert
              2026-08-11). The app header already carries a print icon on every
              page — one way to print the app, not a second one per tab that
              behaves the same and has to be found. This panel is for filling the
              document in; printing it is the header's job. */}
        </div>
      </details>

      {/* The document header itself. Print only — on screen the page already
          says which job you are on, and repeating it is clutter.
          It is the REPORTS masthead, not a second letterhead of its own (Robert
          2026-08-11). The File Life and KPI packs already leave the building
          looking like this — logo, blue title, quiet subtitle, rule underneath —
          and a POW reaching a contractor should look like it came from the same
          company as those. Growing a parallel header here is how two house
          styles start. `report` / `title` / `subtitle` map straight onto what
          this panel collects. */}
      {/* .report-doc is REQUIRED, not decoration: it defines --report-brand and
          the other tokens that .report-masthead then uses for the blue title and
          the 3px rule under it. Rendering the masthead outside it leaves those
          custom properties undefined, so the title greys out and the rule
          disappears — which is exactly what happened on the first cut (Robert
          2026-08-11: "where is that nice blue line"). */}
      <div className="report-doc d-none d-print-block mb-3">
        {/* subtitle = the standing context line, exactly as the report packs use
            it ("Grayburn Property Services · 01-08-2026 to 11-08-2026"). The
            NOTE is not that — it is a covering message and can run to several
            lines, so it prints under the masthead as body text where it can
            wrap, rather than being squeezed into a muted 0.95rem subtitle. */}
        <ReportMasthead title={title} subtitle={subtitle} />
        {/* .report-body, so the note lines up under the masthead text rather
            than flush against the page edge — the masthead carries 1.5rem of
            side padding and a note without it reads as a separate document. */}
        {note ? (
          <div className="report-body" style={{ whiteSpace: "pre-wrap" }}>{note}</div>
        ) : null}
      </div>
    </>
  );
}

export default PrintHeader;
